<template>
  <header>
    <section class="left">
      <icon-button
        class="menu"
        icon="menu"
        @click="$store.commit('SET_NAV_OPEN', !navOpen)"
      />
    </section>

    <section :class="['middle', { open: searchOpen }]">
      <global-search ref="cardSearch" />

      <icon-button
        class="close-search-icon"
        icon="close"
        @click="searchOpen = false"
      />
    </section>

    <section class="right">
      <icon-button
        class="nav-icon search-icon"
        icon="search"
        @click="openSearch"
      />

      <icon-button
        class="nav-icon"
        icon="help_outline"
        as="a"
        href="https://cardladder.zendesk.com/hc/en-us"
        rel="noopener noreferrer"
        target="_blank"
        tooltip="Help"
      />

      <icon-button
        class="nav-icon"
        icon="add_circle"
        tooltip="Submit Sale"
        @click="submitSale"
      />

      <icon-button
        v-if="isStaff"
        class="nav-icon"
        icon="assignment"
        as="router-link"
        tooltip="Reports"
        to="/reports"
      />

      <icon-button
        class="nav-icon medium-up"
        icon="price_check"
        tooltip="Price check"
        @click="showPriceCheckModal = true"
      />

      <icon-button
        class="nav-icon"
        icon="notifications_none"
        tooltip="Notifications"
        :alert="hasUnreadNotifications"
        @click="openNotificationsPanel"
      />

      <dropdown
        v-if="user"
        align="right"
        :active="menuOpen"
        @close="menuOpen = false"
      >
        <avatar
          :image-url="user.photoUrl"
          @click.native="menuOpen = !menuOpen"
        />

        <template #menu>
          <dropdown-menu
            :options="accountOptions"
            :max-height="500"
            @select="handleSelect"
          />
        </template>
      </dropdown>
    </section>

    <portal to="feedback">
      <feedback-modal
        title="Submit Request"
        subtitle="Tell us about the issue you're facing or give feedback"
        :open="showFeedbackModal"
        @close="showFeedbackModal = false"
      />

      <price-check-modal
        :open="showPriceCheckModal"
        @close="showPriceCheckModal = false"
      />
    </portal>
  </header>
</template>

<script>
import firebase from 'firebase/compat/app';
import * as Sentry from '@sentry/vue';

import { mapState, mapGetters } from 'vuex';

import { authService, notificationService } from '@/services';

import GlobalSearch from '@/cards/GlobalSearch';
import FeedbackModal from '@/auth/FeedbackModal';
import PriceCheckModal from '@/sales/PriceCheckModal';

export default {
  components: {
    GlobalSearch,
    FeedbackModal,
    PriceCheckModal,
  },

  data() {
    return {
      searchOpen: false,
      showFeedbackModal: false,
      showPriceCheckModal: false,
      menuOpen: false,
    };
  },

  watch: {
    $route() {
      this.searchOpen = false;
    },
  },

  computed: {
    ...mapState({
      user: (state) => state.user,
      navOpen: (state) => state.navOpen,
      notificationsOpen: (state) => state.notificationsOpen,
    }),

    ...mapGetters({
      isStaff: 'isStaff',
      darkMode: 'darkMode',
    }),

    hasUnreadNotifications() {
      return this.user.hasUnreadNotifications;
    },

    accountOptions() {
      return [
        { value: 'account', label: 'Account', icon: 'account_circle' },
        { value: 'merch', label: 'Merch', icon: 'storefront' },
        { value: 'gift', label: 'Gift', icon: 'redeem' },
        { value: 'feedback', label: 'Submit Request', icon: 'rate_review' },
        { value: 'theme', label: this.darkMode ? 'Light mode' : 'Dark mode', icon: 'settings_brightness' },
        { value: 'logout', label: 'Logout', icon: 'directions_run' },
      ];
    },
  },

  methods: {
    handleSelect(value) {
      this.menuOpen = false;

      if (value === 'logout') {
        if (window.confirm('Are you sure you want to logout?')) {
          this.logout();
        }
      } else if (value === 'account') {
        this.$router.push('/account').catch(() => {});
      } else if (value === 'feedback') {
        this.showFeedbackModal = true;
      } else if (value === 'theme') {
        const change = { theme: this.darkMode ? 'light' : 'dark' };

        authService.updateUser(this.user.uid, change);
      } else if (value === 'merch') {
        window.open('https://cardladder.com/merch', '_blank');
      } else if (value === 'gift') {
        window.open('https://cardladder.com/gift', '_blank');
      }
    },

    async openNotificationsPanel() {
      this.$store.commit('SET_NOTIFICATIONS_OPEN', !this.notificationsOpen);
      this.$store.commit('SET_NAV_OPEN', false);
      await notificationService.setUserReadNotifications(this.user.uid);
    },

    async logout() {
      try {
        this.$store.dispatch('logout');
        await firebase.auth().signOut();

        window.location.replace(import.meta.env.VITE_LANDING);
      } catch (e) {
        this.$toasted.error('Error logging out');
        Sentry.captureException(e);
      }
    },

    openSearch() {
      this.searchOpen = true;

      setTimeout(() => {
        this.$refs.cardSearch.focus();
      });
    },

    submitSale() {
      this.$bus.$emit('add-sale-side');
    },
  },
};
</script>

<style lang="scss" scoped>
header,
.left,
.right,
.middle {
  display: flex;
  align-items: center;
}

header {
  position: fixed;
  height: var(--header-height);
  top: 0;
  left: var(--nav-menu-width);
  right: 0;
  z-index: 6;
  border-bottom: 1px solid var(--theme-border);
  padding: 0 $gp;
  background-color: var(--theme-background);
  justify-content: space-between;

  @media($medium-down) {
    left: 0;
    --avatar-size: 30px;
  }
}

.left {
  line-height: 1;

  @media($medium-up) {
    display: none;
  }
}

.middle {
  height: var(--header-height);
  width: 600px;
  margin-right: $gp;

  @media($medium-down) {
    position: fixed;
    z-index: 2;
    top: 0;
    left: 0;
    right: 0;
    background-color: var(--theme-card);
    padding: 0 $gp * .5 0 0;
    display: none;
    align-items: center;
    width: 100%;
    border-bottom: 1px solid var(--theme-border);
    margin-right: 0;

    .close {
      margin-left: $gp * .25;
    }

    &.open {
      display: flex;
    }
  }
}

.nav-icon {
  margin-right: $gp * .5;

  @media($medium-up) {
    margin-right: $gp;
  }
}

.menu {
  margin-right: $gp * .5;

  @media($medium-up) {
    display: none !important;
  }
}

@media($medium-up) {
  .search-icon,
  .close-search-icon {
    display: none !important;
  }
}

.close-search-icon {
  margin-left: $gp * .5;
}
</style>
